import React, { useEffect, FC, Fragment } from 'react';
import commonStyles from './style/endResultStageCommon.module.scss';
import { useTranslation } from 'react-i18next';
import ConnectionSummaryTile from './ConnectionSummaryTile';
import BankUpdated from './BankUpdated';
import BankConnected from './BankConnected';
import { queryKeys, useConnectionSummary, useConsentInfo, useSettings } from '../../hooks/queryHooks';
import { consentStorage } from '../../domain/services';
import { useAppState } from '../../hooks/zustandHooks';
import { useHistory } from 'react-router';
import { completeConnection } from '../../hooks/utils';
import { useQueryClient } from '@tanstack/react-query';
import Container from '../container/Container';
import InfoBox from '../infoBox/InfoBox';
import ThumbsUpSvg from '../../assets/ThumbsUp';
import { trackEvent } from '../../App';
import { EventTypes } from '../../utils/applicationInsights';

interface IProps {
  isAddAnotherAccountDisabled: boolean;
}

const SuccessScreen: FC<IProps> = ({ isAddAnotherAccountDisabled }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { connectionSummary } = useConnectionSummary();
  const { settings } = useSettings();
  const { appState, setAppState } = useAppState();
  const queryClient = useQueryClient();
  const {
    state,
    rpName,
    reAuthentication,
    isReconfirmation,
    consentRevokeSuccess,
    redirectUrl: redirect_uri,
    consentId
  } = appState;
  const { consentInfo } = useConsentInfo(isReconfirmation);
  const redirectUrl = redirect_uri || settings?.redirectUrl;
  const bankName = state?.ProviderName ?? '';
  const companyName = state?.ApplicationName ?? rpName;
  const consentEnd = consentStorage.getConsentEnd();
  const extendedConsentEnd = isReconfirmation && consentEnd?.consentEnd ? new Date(consentEnd.consentEnd) : undefined;

  const addAnotherAccountBtn = () => {
    // reset state
    queryClient.removeQueries({ queryKey: [queryKeys.consent] });
    queryClient.removeQueries({ queryKey: [queryKeys.consentConfiguration] });
    queryClient.removeQueries({ queryKey: [queryKeys.consentInfo] });
    setAppState({
      reAuthentication: false,
      isReconfirmation: false,
      consentRevokeSuccess: undefined,
      consentId: undefined
    });
    history.push('/bank-select');
  };

  useEffect(() => {
    if (isReconfirmation || consentRevokeSuccess || reAuthentication) return;
    trackEvent({
      type: EventTypes.COMPLETE_CONNECTION,
      payload: { consentId }
    });
    if (window.hj) window.hj('tagRecording', ['Screen: Connection Success']);
  }, []);

  useEffect(() => {
    if (window.hj) {
      if (redirectUrl) {
        window.hj('tagRecording', ['Redirect']);
      }

      if (window.opener != null) {
        window.hj('tagRecording', ['Pop-up']);
      }
    }
  }, [redirectUrl]);

  const connectionContent = () => {
    if (consentRevokeSuccess) {
      return (
        <div>
          <h1 className="uppercase text-md font-semi-bold mb-2">{t('Access Revoked')}</h1>
          <p>{t('Access to your bank account has been revoked successfully.')}</p>
        </div>
      );
    }

    if (!consentRevokeSuccess) {
      return (
        <>
          {reAuthentication ? (
            <BankUpdated bankName={bankName} companyName={companyName} extendedConsentEnd={extendedConsentEnd} />
          ) : isReconfirmation ? (
            <BankUpdated
              bankName={consentInfo?.providerName || ''}
              companyName={consentInfo?.customerName || ''}
              extendedConsentEnd={consentInfo?.consentEndDate}
            />
          ) : (
            <BankConnected />
          )}
          {connectionSummary && !reAuthentication && (
            <div className={commonStyles.connectionSummaryTilesWrap} data-testid="bank-connection-summary">
              {connectionSummary.accounts.map((acc, idx) => (
                <Fragment key={idx}>
                  <ConnectionSummaryTile account={acc} bankName={bankName} />
                  {connectionSummary.accounts.length > 1 && idx !== connectionSummary.accounts.length - 1 && (
                    <hr className="text-gray-300 mx-4 sm:mx-6"></hr>
                  )}
                </Fragment>
              ))}
            </div>
          )}
        </>
      );
    }
  };

  return (
    <Container
      {...(!isAddAnotherAccountDisabled && {
        primaryBtn: {
          text: t('Add another account'),
          onClick: addAnotherAccountBtn
        }
      })}
      {...((!!redirectUrl || window.opener != null) && {
        secondaryBtn: {
          text: redirectUrl ? t('Continue') : t('Close window'),
          onClick: completeConnection
        }
      })}
    >
      <div data-testid="bank-connection-success-screen">
        <div className={commonStyles.topWrapper}>{connectionContent()}</div>
        <div className="mt-6">
          {redirectUrl && !isAddAnotherAccountDisabled ? (
            <InfoBox icon={<ThumbsUpSvg />}>
              {t(
                'You may now close this window if you have no other accounts to add at this time, or follow the links below to add another bank, and manage your consents.'
              )}
            </InfoBox>
          ) : (
            t('You may now close this window.')
          )}
        </div>
      </div>
    </Container>
  );
};

export default SuccessScreen;
